import React, { memo, useEffect } from 'react'
import { useAppDispatch } from '../../../hooks/useAppDispatch'
import { useFormValidation } from '../../../hooks/useFormValidation'
import { buildControlsExt, dateTimePicker } from '../../inputs/controls'
import { setTimeFormat } from './utils'
import { inputValidationCheck } from '../../../validators/inputValidators/inputValidators'
import { datePickerErrors } from '../../../utils/validationErrors/datePickerErrors'
import { checkUnsavedChanges } from '../../../store/actions/configurationActions'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { configsActions } from '../../../store/slices/configsSlice'
import { initEmptyData } from '../../../store/actions/modelsActions/control'
import { PATH_JOINER } from '../utils/getConfigBitPath'
import {
    selectConfigBit,
    selectConfigIsChecked,
} from '../../../store/selectors/configSelectors'

export interface IDateTimeProps {
    dataPath: string
    pathWithElementID: string | undefined
    dataSchema: any
    name: string
    isParentDisabled?: boolean
}

export const DateTime = memo(function DateTime({
    dataPath,
    pathWithElementID,
    dataSchema = {},
    name,
    isParentDisabled,
}: IDateTimeProps) {
    const dispatch = useAppDispatch()
    const {
        greater_or_equal,
        greater_than,
        less_or_equal,
        less_than,
        default: defaultValue,
        format: timeSchema,
        description,
        is_required: isRequired,
        type,
        time_source: timeSource,
        change_warning: changeWarning,
    } = dataSchema
    const configBit = useAppSelector(
        selectConfigBit(dataPath.split(PATH_JOINER))
    )
    const path = [...dataPath.split(PATH_JOINER), name]
    const [guid, , tabName] = path
    const errorKey = pathWithElementID
        ? [...pathWithElementID.split(PATH_JOINER), name].join(PATH_JOINER)
        : path.join(PATH_JOINER)
    const isChecked = useAppSelector(selectConfigIsChecked)
    const validationCheck = inputValidationCheck(type, dataSchema)
    const [
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
        isValid,
    ] = useFormValidation(configBit, { [name]: validationCheck })
    const minDate = greater_or_equal || greater_than
    const maxDate = less_or_equal || less_than
    const isDataEmpty: number = Object.keys(configBit || {}).length
    const isUndefined: boolean = typeof configBit?.[name] === 'undefined'

    const errMsg = datePickerErrors(inputState?.[name], dataSchema)
    const timeFormat = setTimeFormat(timeSchema)

    // This effect highlights invalid input on Apply
    useEffect(() => {
        const isProductConfigsChecked = isChecked[guid]
        const isParentBlockNotDisabled /* has non-empty path */ =
            dataPath.length
        if (isProductConfigsChecked && isParentBlockNotDisabled) {
            isValid()
        }
    }, [isChecked, guid, isChecked, dataPath.length])

    useEffect(() => {
        if (errMsg && path.length > 2) {
            dispatch(
                configsActions.setConfigInvalid({
                    guid,
                    tabName,
                    errorKeyName: errorKey,
                })
            )
        }
        if (!errMsg && path.length > 2) {
            dispatch(
                configsActions.setConfigValid({
                    guid,
                    tabName,
                    errorKeyName: errorKey,
                })
            )
        }
    }, [inputState?.[name], dispatch])

    useEffect(() => {
        if (configBit && dataPath.length) {
            setInputState(configBit)
        }
    }, [configBit]) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            if (!configBit || !isDataEmpty || isUndefined) {
                dispatch(initEmptyData({ schema: dataSchema, name, path }))
            }
            if (defaultValue && !configBit[name]) {
                setInputState({ ...configBit, [name]: defaultValue })
            }
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            if (!configBit || !isDataEmpty || isUndefined) {
                dispatch(initEmptyData({ schema: dataSchema, name, path }))
            }
            if (JSON.stringify(configBit) !== JSON.stringify(inputState)) {
                dispatch(
                    configsActions.modifyDateTime({
                        componentState: { [name]: inputState?.[name] },
                        path,
                        data: configBit,
                        name,
                    })
                )
                dispatch(checkUnsavedChanges(path[0]))
            }
        }
    }, [inputState, touched]) // eslint-disable-line

    const controlDescriptors = [
        dateTimePicker(
            name,
            {
                timeFormat,
                timeSchema,
                timeSource,
                description,
                isColumn: true,
                minDate,
                maxDate,
                errMsg,
                isParentDisabled,
                changeWarning
            },
            isRequired
        ),
    ]

    const DateTimeInput = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    return <div className="w-100">{DateTimeInput}</div>
})
